import { ChangeDetectionStrategy, Component } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { translate } from '@jsverse/transloco';
import { Store } from '@ngrx/store';
import { SubscriptionComponent } from '@liebherr/old-sage-ui';
import { Observable } from 'rxjs';
import { first, map, withLatestFrom } from 'rxjs/operators';
import { FirmwareUpdateState, RouterCreationDto, RouterTypeDto, VpnLinkedState } from '../api/core';

import { isNil } from 'lodash';
import { AppState } from '../app.models';
import * as fromRouter from '../router';
import { selectRouterTypes } from '../router-types/router-types.selectors';
import { DeleteDialogComponent } from '../shared/components/delete-dialog/delete-dialog.component';
import { ColumnGroup } from '../shared/directives/table-config.directive';
import { DeleteDlgData, FilterGroup, FilterType } from '../shared/shared.models';
import { selectTenants } from '../tenants';
import { LoggedInUser } from '../users';
import * as userSelectors from '../users/users.selectors';
import { DialogWidths, registerApiCall } from '../utils';
import * as fromDevices from './';
import { DeviceMenuAction, Router, getDevicePermissions } from './devices.models';
import { AddDevicesDialogComponent } from './dialogs/add-devices-dialog/add-devices-dialog.component';
import { EditDevicesDialogComponent } from './dialogs/edit-devices-dialog/edit-devices-dialog.component';
import { FirmwareDialogComponent } from './dialogs/firmware-dialog/firmware-dialog.component';
import { MasterPasswordDialogComponent } from './dialogs/master-password/master-password.component';

@Component({
    selector: 'liebherr-devices',
    templateUrl: 'devices.component.html',
    styleUrls: ['devices.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DevicesComponent extends SubscriptionComponent {
    getDevicePermissions = getDevicePermissions;

    loggedInUser$: Observable<LoggedInUser> = this.store.select(userSelectors.selectLoggedInUser);

    filterParams$ = this.store.select(fromRouter.selectFilterParams);
    loading$ = this.store.select(fromDevices.selectLoading);
    devices$ = this.store.select(fromDevices.selectDevices);
    deviceCount$ = this.store.select(fromDevices.selectCount);
    routerTypes$ = this.store.select(selectRouterTypes);
    tenants$ = this.store.select(selectTenants);
    filterGroups$: Observable<FilterGroup[]> = this.routerTypes$.pipe(map((routerTypes) => this.getFilterGroups(routerTypes)));

    columnGroups: ColumnGroup[] = this.createColumnGroups();

    COLUMNS = fromDevices.DeviceColumns;

    columnsWithSorting = [
        fromDevices.DeviceColumns.ONLINE,
        fromDevices.DeviceColumns.CRANE_ID,
        fromDevices.DeviceColumns.C_FETCHED_TIME,
        fromDevices.DeviceColumns.C_GRANTED_BY,
        fromDevices.DeviceColumns.C_GRANTED_TIME,
        fromDevices.DeviceColumns.C_GRANTED_UNTIL,
        fromDevices.DeviceColumns.C_REQUEST_TIME,
        fromDevices.DeviceColumns.ROUTER_TYPE,
        fromDevices.DeviceColumns.TENANT,
        // fromDevices.DeviceColumns.DEVICE_ID, // deviceId = imei, same column as serial number
        fromDevices.DeviceColumns.FW_DOWNLOAD_ALLOWED_BY,
        fromDevices.DeviceColumns.FW_DOWNLOAD_ALLOWED_TIME,
        fromDevices.DeviceColumns.FW_DOWNLOAD_TIME,
        fromDevices.DeviceColumns.FW_INSTALLED_TIME,
        fromDevices.DeviceColumns.FW_TARGET_VERSION,
        fromDevices.DeviceColumns.SERIAL_NUMBER,
        fromDevices.DeviceColumns.SIM_CARD_EXPIRATION_DATE,
    ];

    constructor(private store: Store<AppState>, public dialog: MatDialog, public routerService: fromRouter.RouterService) {
        super();
    }

    onAddDevice() {
        const dialogRef = this.dialog.open<AddDevicesDialogComponent, any>(AddDevicesDialogComponent, {
            disableClose: true,
        });
        dialogRef.afterClosed().subscribe((data?: RouterCreationDto | RouterCreationDto[]) => {
            if (data) {
                if (Array.isArray(data)) {
                    // add devices bulk
                    registerApiCall(
                        () =>
                            this.store.dispatch(
                                fromDevices.addDevicesBulk({
                                    devices: data as RouterCreationDto[],
                                })
                            ),
                        fromDevices.DeviceActionTypes.ADDED_DEVICES_BULK
                    )
                        .pipe(first(), withLatestFrom(this.filterParams$))
                        .subscribe(([action, filterParams]) => this.reloadDevices(filterParams));
                } else {
                    // add single device
                    registerApiCall(
                        () =>
                            this.store.dispatch(
                                fromDevices.addDevice({
                                    device: data as RouterCreationDto,
                                })
                            ),
                        fromDevices.DeviceActionTypes.ADDED_DEVICE
                    )
                        .pipe(first(), withLatestFrom(this.filterParams$))
                        .subscribe(([action, filterParams]) => this.reloadDevices(filterParams));
                }
            }
        });
    }

    onActionMenuClick(event: string, element: Router) {
        switch (event) {
            case DeviceMenuAction.ACTIVATE_DEVICE: {
                this.onDeviceActivation(element);
                break;
            }
            case DeviceMenuAction.CERTIFICATE_RENEWAL: {
                this.onCertificateRenewal(element);
                break;
            }
            case DeviceMenuAction.DELETE_DEVICE: {
                this.onDeleteDevice(element);
                break;
            }
            case DeviceMenuAction.FW_ACTIVATION: {
                this.openFwDialog(element, 'activate');
                break;
            }
            case DeviceMenuAction.FW_DONWLOAD: {
                this.openFwDialog(element, 'download');
                break;
            }
            case DeviceMenuAction.FW_FORCE: {
                this.openFwDialog(element, 'force');
                break;
            }

            case DeviceMenuAction.MASTER_PASSWORT: {
                this.onMasterPassword(element);
                break;
            }
            case DeviceMenuAction.VPN_DISCONNECT: {
                this.onVpnDisconnect(element);
                break;
            }
            case DeviceMenuAction.EDIT: {
                this.openEditDialog(element);
                break;
            }

            default: {
                break;
            }
        }
    }

    private getFilterGroups(routerTypes: RouterTypeDto[]): FilterGroup[] {
        return [
            {
                title: 'DEVICES.DEVICES',
                controls: [
                    {
                        type: FilterType.SEARCH,
                        label: 'DEVICES.SERIALNUMBER',
                        paramsKey: fromRouter.ParamsKey.SERIAL_NUMBER,
                    },
                    {
                        type: FilterType.SEARCH,
                        label: 'DEVICES.CRANE_NUMBER',
                        paramsKey: fromRouter.ParamsKey.CRANE_NUMBER,
                    },
                    {
                        type: FilterType.SEARCH,
                        label: 'DEVICES.VPN_IP',
                        paramsKey: fromRouter.ParamsKey.VPN_IP,
                    },
                    {
                        type: FilterType.SELECTION,
                        label: 'DEVICES.CONNECTION_STATE',
                        paramsKey: fromRouter.ParamsKey.CONNECTION_STATE,
                        options: Object.keys(VpnLinkedState).map((k) => ({
                            value: VpnLinkedState[k],
                            label: 'DEVICES.CONNECTION.' + VpnLinkedState[k],
                        })),
                    },
                    {
                        type: FilterType.SELECTION,
                        label: 'DEVICES.ONLINE_STATE',
                        paramsKey: fromRouter.ParamsKey.ONLINE_STATE,
                        options: ['ONLINE', 'OFFLINE'].map((k) => ({
                            value: k,
                            label: 'DEVICES.CONNECTION.' + k,
                        })),
                    },
                    {
                        type: FilterType.SELECTION,
                        label: 'DEVICES.ROUTER_TYPE',
                        paramsKey: fromRouter.ParamsKey.ROUTER_TYPE,
                        options: routerTypes.map((r) => ({
                            value: r.id,
                            label: r.name || 'NO NAME',
                        })),
                    },
                ],
            },
            {
                title: 'DEVICES.FIRMWARES',
                controls: [
                    {
                        type: FilterType.SEARCH,
                        label: 'DEVICES.INSTALLED_FW',
                        paramsKey: fromRouter.ParamsKey.INSTALLED_FW,
                    },
                    {
                        type: FilterType.SEARCH,
                        label: 'DEVICES.TARGET_FW',
                        paramsKey: fromRouter.ParamsKey.TARGET_FW,
                    },
                    {
                        type: FilterType.SELECTION,
                        label: 'DEVICES.FW_STATE',
                        paramsKey: fromRouter.ParamsKey.FW_STATE,
                        options: Object.keys(FirmwareUpdateState).map((k) => ({
                            value: FirmwareUpdateState[k],
                            label: 'DEVICES.FIRMWARE.STATE.' + FirmwareUpdateState[k],
                        })),
                    },
                    {
                        type: FilterType.SEARCH,
                        label: 'DEVICES.FW_RELEASED_BY',
                        paramsKey: fromRouter.ParamsKey.FW_RELEASED_BY,
                    },
                ],
            },
        ];
    }

    private reloadDevices(params: fromRouter.FilterParams) {
        this.store.dispatch(
            fromDevices.loadDevices({
                params,
            })
        );
    }

    private onMasterPassword(device: Router) {
        if (device.id) {
            const dialogRef = this.dialog.open<MasterPasswordDialogComponent, Router>(MasterPasswordDialogComponent, {
                data: device,
                width: DialogWidths.SMALL,
            });
        }
    }

    private onDeviceActivation(device: Router) {
        if (device.id) {
            this.store.dispatch(fromDevices.activateDevice({ id: device.id }));
        }
    }
    private onCertificateRenewal(device: Router) {
        if (device.id) {
            this.store.dispatch(fromDevices.allowCertificateRenewal({ id: device.id }));
        }
    }
    private openFwDialog(device: Router, type: 'download' | 'activate' | 'force') {
        const dialogRef = this.dialog.open<FirmwareDialogComponent, { device: Router; type: 'download' | 'activate' | 'force' }>(
            FirmwareDialogComponent,
            {
                data: { device, type },
                disableClose: true,
                width: DialogWidths.SMALL,
            }
        );
        dialogRef.afterClosed().subscribe((data: boolean | number) => {
            if ((data === true || typeof data === 'number') && device.id) {
                switch (type) {
                    case 'activate': {
                        this.store.dispatch(fromDevices.allowFwActivation({ id: device.id }));
                        break;
                    }
                    case 'download': {
                        this.store.dispatch(fromDevices.allowFwDownload({ id: device.id }));
                        break;
                    }
                    case 'force': {
                        if (device.id && typeof data === 'number') {
                            this.store.dispatch(
                                fromDevices.forceFwUpdate({
                                    routerId: device.id,
                                    firmwareId: data,
                                })
                            );
                        }
                        break;
                    }

                    default: {
                        break;
                    }
                }
            }
        });
    }

    private openEditDialog(device: Router) {
        const dialogRef = this.dialog.open<EditDevicesDialogComponent, { device: Router }>(EditDevicesDialogComponent, {
            data: { device },
            width: DialogWidths.SMALL,
        });
        dialogRef.afterClosed().subscribe((data) => {
            if (data) {
                this.store.dispatch(
                    fromDevices.editDevice({
                        device: { ...data, id: device.id },
                    })
                );
            }
        });
    }

    private onVpnDisconnect(device: Router) {
        if (!isNil(device.id)) {
            registerApiCall(
                () =>
                    this.store.dispatch(
                        fromDevices.disconnectDeviceFromVPN({
                            id: device.id!,
                            connectedUser: device.vpnInfo.currentUser!,
                        })
                    ),
                fromDevices.DeviceActionTypes.DISCONNECTED_DEVICE
            )
                .pipe(first(), withLatestFrom(this.filterParams$))
                .subscribe(([action, filterParams]) => this.reloadDevices(filterParams));
        }
    }

    private onDeleteDevice(device: Router) {
        const dialogRef = this.dialog.open<DeleteDialogComponent, DeleteDlgData>(DeleteDialogComponent, {
            data: {
                title: translate('DEVICES.DELETE_TITLE'),
                hint: translate('DEVICES.DELETE_HINT', {
                    serial: device.serialNumber,
                }),
            },
            disableClose: true,
            width: DialogWidths.SMALL,
        });
        dialogRef.afterClosed().subscribe((data: boolean) => {
            if (data === true && device.id !== undefined) {
                registerApiCall(
                    () => this.store.dispatch(fromDevices.deleteDevice({ id: device.id! })),
                    fromDevices.DeviceActionTypes.DELETED_DEVICE
                )
                    .pipe(first(), withLatestFrom(this.filterParams$))
                    .subscribe(([action, filterParams]) => this.reloadDevices(filterParams));
            }
        });
    }

    private createColumnGroups() {
        const order = [
            fromDevices.DeviceColumns.ONLINE,
            fromDevices.DeviceColumns.SERIAL_NUMBER,
            fromDevices.DeviceColumns.CRANE_ID,
            fromDevices.DeviceColumns.LINK,
            fromDevices.DeviceColumns.SIM_CARD_EXPIRATION_DATE,
            fromDevices.DeviceColumns.CONNECTED_USER,
            fromDevices.DeviceColumns.TENANT,
            fromDevices.DeviceColumns.STATE_TIME,
            fromDevices.DeviceColumns.ROUTER_TYPE,
            fromDevices.DeviceColumns.VPN_IP,
            fromDevices.DeviceColumns.C_COMBINED,
            fromDevices.DeviceColumns.C_REQUEST_TIME,
            fromDevices.DeviceColumns.C_GRANTED_TIME,
            fromDevices.DeviceColumns.C_GRANTED_UNTIL,
            fromDevices.DeviceColumns.C_GRANTED_BY,
            fromDevices.DeviceColumns.C_FETCHED_TIME,
            fromDevices.DeviceColumns.FW_COMBINED,
            fromDevices.DeviceColumns.FW_TARGET_VERSION,
            fromDevices.DeviceColumns.FW_DOWNLOAD_ALLOWED_TIME,
            fromDevices.DeviceColumns.FW_DOWNLOAD_ALLOWED_BY,
            fromDevices.DeviceColumns.FW_DOWNLOAD_TIME,
            fromDevices.DeviceColumns.FW_INSTALLED_TIME,
            fromDevices.DeviceColumns.ROUTER_STATE,
            fromDevices.DeviceColumns.ACTIONS,
        ];

        const defaultGroup: ColumnGroup = {
            columns: [],
        };
        [
            fromDevices.DeviceColumns.ONLINE,
            fromDevices.DeviceColumns.SERIAL_NUMBER,
            fromDevices.DeviceColumns.CRANE_ID,
            fromDevices.DeviceColumns.LINK,
            fromDevices.DeviceColumns.SIM_CARD_EXPIRATION_DATE,
            fromDevices.DeviceColumns.CONNECTED_USER,
            fromDevices.DeviceColumns.STATE_TIME,
            fromDevices.DeviceColumns.ROUTER_TYPE,
            fromDevices.DeviceColumns.VPN_IP,
            fromDevices.DeviceColumns.ROUTER_STATE,
            fromDevices.DeviceColumns.ACTIONS,
            fromDevices.DeviceColumns.TENANT,
        ].forEach((col) => {
            defaultGroup.columns.push({
                key: col,
                label: 'DEVICES.COLUMNS.' + col.toUpperCase(),
                visible: true,
                toggleable: col !== fromDevices.DeviceColumns.ACTIONS && col !== fromDevices.DeviceColumns.ONLINE,
                position: order.indexOf(col),
            });
        });

        const certificateGroup: ColumnGroup = {
            title: 'DEVICES.COLUMNS.C_COMBINED',
            columns: [],
        };
        [
            fromDevices.DeviceColumns.C_COMBINED,
            fromDevices.DeviceColumns.C_REQUEST_TIME,
            fromDevices.DeviceColumns.C_GRANTED_TIME,
            fromDevices.DeviceColumns.C_GRANTED_UNTIL,
            fromDevices.DeviceColumns.C_GRANTED_BY,
            fromDevices.DeviceColumns.C_FETCHED_TIME,
        ].forEach((col) => {
            certificateGroup.columns.push({
                key: col,
                label: 'DEVICES.COLUMNS.' + col.toUpperCase(),
                visible: col === fromDevices.DeviceColumns.C_COMBINED,
                toggleable: col !== fromDevices.DeviceColumns.C_COMBINED,
                position: order.indexOf(col),
            });
        });
        const firmwarGroup: ColumnGroup = {
            title: 'DEVICES.COLUMNS.FW_COMBINED',
            columns: [],
        };
        [
            fromDevices.DeviceColumns.FW_COMBINED,
            fromDevices.DeviceColumns.FW_TARGET_VERSION,
            fromDevices.DeviceColumns.FW_DOWNLOAD_ALLOWED_TIME,
            fromDevices.DeviceColumns.FW_DOWNLOAD_ALLOWED_BY,
            fromDevices.DeviceColumns.FW_DOWNLOAD_TIME,
            fromDevices.DeviceColumns.FW_INSTALLED_TIME,
        ].forEach((col) => {
            firmwarGroup.columns.push({
                key: col,
                label: 'DEVICES.COLUMNS.' + col.toUpperCase(),
                visible: col === fromDevices.DeviceColumns.FW_COMBINED,
                toggleable: col !== fromDevices.DeviceColumns.FW_COMBINED,
                position: order.indexOf(col),
            });
        });

        return [defaultGroup, certificateGroup, firmwarGroup];
    }
}
